import {useCallback, useRef, useState} from "react";
import {Box, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import AddCommentRoundedIcon from "@mui/icons-material/AddCommentRounded";
import {alpha} from "@mui/material/styles";
import {useIsMutating} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {chatEditMutationKey, chatIndexMutationKey, useChatHistory} from "../../api/chat/chatQueries";
import {getChatPath} from "../../routes";
import {PendingChatUpload} from "../../util/types";
import ChatHistoryComponent, {chatHistoryDrawerWidth} from "../chathistory/chathistory";
import ChatBox from "./ChatBox";
import useChatAttachments from "./useChatAttachments";
import useFileDropzone from "./useFileDropzone";

const newChatAttachmentKey = "new-chat";

type AttachmentDropOverlayProps = {
    dropPrompt: string;
};

const AttachmentDropOverlay: React.FC<AttachmentDropOverlayProps> = ({dropPrompt}) => (
    <Box
        sx={(theme) => ({
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: chatHistoryDrawerWidth,
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderTop: "2px dashed",
            borderRight: "2px dashed",
            borderBottom: "2px dashed",
            borderLeft: "2px dashed",
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            backdropFilter: "blur(2px)",
            pointerEvents: "none",
        })}
    >
        <Typography
            sx={(theme) => ({
                px: 3,
                py: 1.5,
                borderRadius: 999,
                bgcolor: alpha(theme.palette.background.paper, 0.92),
                color: theme.palette.text.primary,
                fontWeight: 600,
            })}
            variant="body1"
        >
            {dropPrompt}
        </Typography>
    </Box>
);

const ChatComponent: React.FC = () => {
    const {chatId: selectedChatId} = useParams<{chatId: string}>();
    const [attachmentsByChatKey, setAttachmentsByChatKey] = useState<Record<string, PendingChatUpload[]>>({});
    const chatPaneRef = useRef<HTMLDivElement | null>(null);
    const {t: translate} = useTranslation();
    const navigate = useNavigate();
    const activeChatIndexMutations = useIsMutating({mutationKey: chatIndexMutationKey});
    const activeChatEditMutations = useIsMutating({mutationKey: chatEditMutationKey});
    const isChatProcessing = activeChatIndexMutations > 0 || activeChatEditMutations > 0;
    const chatHistoryQuery = useChatHistory(selectedChatId);

    const clearNewChatAttachments = useCallback(() => {
        setAttachmentsByChatKey((previousAttachmentsByChatKey) => {
            if (!(newChatAttachmentKey in previousAttachmentsByChatKey)) {
                return previousAttachmentsByChatKey;
            }

            const {[newChatAttachmentKey]: removedNewChatAttachments, ...remainingAttachmentsByChatKey} =
                previousAttachmentsByChatKey;
            void removedNewChatAttachments;
            return remainingAttachmentsByChatKey;
        });
    }, []);

    const handleNewChat = () => {
        if (!selectedChatId || isChatProcessing) {
            return;
        }

        clearNewChatAttachments();
        navigate("/");
    };

    const handleChatDeleted = useCallback((deletedChatId: string) => {
        setAttachmentsByChatKey((previousAttachmentsByChatKey) => {
            if (!(deletedChatId in previousAttachmentsByChatKey)) {
                return previousAttachmentsByChatKey;
            }

            const {[deletedChatId]: deletedChatAttachments, ...remainingAttachmentsByChatKey} =
                previousAttachmentsByChatKey;
            void deletedChatAttachments;
            return remainingAttachmentsByChatKey;
        });

        if (deletedChatId === selectedChatId) {
            navigate("/");
        }
    }, [navigate, selectedChatId]);

    const handleLoadChat = useCallback((chatId: string | undefined) => {
        if (!selectedChatId) {
            clearNewChatAttachments();
        }

        navigate(chatId ? getChatPath(chatId) : "/");
    }, [clearNewChatAttachments, navigate, selectedChatId]);

    const handleChatCreated = useCallback((chatId: string) => {
        setAttachmentsByChatKey((previousAttachmentsByChatKey) => {
            const newChatAttachments = previousAttachmentsByChatKey[newChatAttachmentKey];
            if (!newChatAttachments?.length) {
                return previousAttachmentsByChatKey;
            }

            const {[newChatAttachmentKey]: removedNewChatAttachments, ...remainingAttachmentsByChatKey} =
                previousAttachmentsByChatKey;
            void removedNewChatAttachments;

            return {
                ...remainingAttachmentsByChatKey,
                [chatId]: newChatAttachments,
            };
        });
        navigate(getChatPath(chatId));
    }, [navigate]);

    const handleAttachmentsChange = useCallback((attachmentKey: string, attachments: PendingChatUpload[]) => {
        setAttachmentsByChatKey((previousAttachmentsByChatKey) => {
            if (attachments.length === 0) {
                if (!(attachmentKey in previousAttachmentsByChatKey)) {
                    return previousAttachmentsByChatKey;
                }

                const {
                    [attachmentKey]: removedAttachments,
                    ...remainingAttachmentsByChatKey
                } = previousAttachmentsByChatKey;
                return remainingAttachmentsByChatKey;
            }

            return {
                ...previousAttachmentsByChatKey,
                [attachmentKey]: attachments,
            };
        });
    }, []);

    const attachmentKey = selectedChatId ?? newChatAttachmentKey;
    const attachments = attachmentsByChatKey[attachmentKey] ?? [];
    const documents = chatHistoryQuery.data?.documents ?? [];
    const {
        attachmentNotice,
        clearAttachmentNotice,
        handleAttachmentFiles,
        handleRemoveDocument,
    } = useChatAttachments({
        attachmentKey,
        attachments,
        chatId: selectedChatId,
        documents,
        onAttachmentsChange: handleAttachmentsChange,
        onChatCreated: handleChatCreated,
    });
    const {
        dropzoneHandlers,
        isDragActive,
    } = useFileDropzone({onFilesDropped: (files) => void handleAttachmentFiles(files)});

    return (
        <Box
            {...dropzoneHandlers}
            sx={{position: "relative", height: "100%", overflow: "hidden"}}
        >
            <Box sx={{position: "absolute", top: 10, right: 24, display: "flex", gap: 1, alignItems: "center"}}>
                <Button
                    onClick={handleNewChat}
                    disabled={!selectedChatId || isChatProcessing}
                    variant="contained"
                    startIcon={<AddCommentRoundedIcon />}
                    sx={(theme) => ({
                        minHeight: 40,
                        px: 2,
                        borderRadius: 999,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        border: "1px solid",
                        borderColor: alpha(theme.palette.primary.dark, 0.24),
                        boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.24)}`,
                        "&:hover": {
                            bgcolor: theme.palette.primary.dark,
                            boxShadow: `0 14px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                        },
                        "& .MuiButton-startIcon": {
                            marginRight: 0.75,
                        },
                    })}
                >
                    {translate("chat.newChat")}
                </Button>
            </Box>
            {isDragActive ? <AttachmentDropOverlay dropPrompt={translate("chat.attachments.dropPrompt")}/> : null}
            <ChatHistoryComponent
                loadChat={handleLoadChat}
                onChatDeleted={handleChatDeleted}
                selectedChatId={selectedChatId}
            />
            <Box
                ref={chatPaneRef}
                sx={{
                    marginLeft: chatHistoryDrawerWidth,
                    width: `calc(100% - ${chatHistoryDrawerWidth})`,
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    minWidth: 0,
                    boxSizing: "border-box",
                    px: 2,
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >
                <ChatBox
                    key={selectedChatId ?? "new"}
                    attachmentNotice={attachmentNotice}
                    chat={chatHistoryQuery.data}
                    chatHistoryError={chatHistoryQuery.error?.message}
                    chatId={selectedChatId}
                    clearAttachmentNotice={clearAttachmentNotice}
                    isChatHistoryLoading={Boolean(selectedChatId) && !chatHistoryQuery.data && chatHistoryQuery.isFetching}
                    isChatProcessing={isChatProcessing}
                    onChatCreated={handleChatCreated}
                    onFilesSelected={(files) => void handleAttachmentFiles(files)}
                    onRemoveDocument={(documentId) => void handleRemoveDocument(documentId)}
                    pendingUploads={attachments}
                    scrollContainerRef={chatPaneRef}
                />
            </Box>
        </Box>
    );
};

export default ChatComponent;
